<script context="module" lang="ts">
	import Section from '$lib/components/layout/Section.svelte';
	import Media from '$lib/components/media/Media.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';

	export const config: SectionConfig = {
		collection: 'section_text_image',
		availableFor: ['pages'],
		query: gql`
        is_slider
        media_position
        type
        id
        button_link {
            ...Link
        }
        translations {
            languages_code {
                code
            }
            section_title
            title
            text
        }
        items {
            section_text_image_item_id {
                media {
                    ...Media
                }
				translations {
					languages_code {
						code
					}
					media_caption
				}
            }
        }
        `
	};
	type Props = {
		section_title: string;
		title: string;
		text: string;
		media: any;
		media_position: any;
		items: any;
		is_slider: boolean;
		type: any;
		button_link: any;
		id: string;
	};
</script>

<script lang="ts">
	import Wysiwyg from '$lib/components/inputs/WYSIWYG.svelte';
	import Slider from '$lib/components/util/Slider.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import Button from '$lib/components/util/Button.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
</script>

<div class="section-text-image">
	<Section gapType="padding">
		<div
			class="background {data.media_position} {data.type} {data.items[0].section_text_image_item_id
				.media.type === 'video/mp4'
				? 'video-section'
				: ''}"
		/>
		{#if data.is_slider}
			<div class="section-wrapper {data.type}">
				{#if data.items}
					{#if data.type === 'large'}
						<div class="container">
							<div class="col-large">
								<div>
									{#if data.section_title}
										<div class="section-title">{data.section_title}</div>
									{/if}
									{#if data.title}
										<div class="title {data.type}">{data.title}</div>
									{/if}
									{#if data.text}
										<div class="text-large-mobile">
											<Wysiwyg text={data.text} />
										</div>
									{/if}
								</div>

								<Slider
									name="text-image-{data.id}"
									type="single"
									navigation={true}
									pagination={true}
									slidesPerView={1}
									centeredSlides={false}
									arrowType="arrow"
								>
									{#each data.items as { section_text_image_item_id: item }, idx}
										<div class="swiper-slide">
											<div class="wrapper">
												<div class="media">
													<Media {...item.media} fit="cover" transform="xl" />
												</div>
												{#if item.media_caption}
													<div class="media-caption">
														{item.media_caption}
													</div>
												{/if}
											</div>
										</div>
									{/each}
								</Slider>
							</div>
							<div>
								{#if data.text}
									<div class="text-large">
										<Wysiwyg text={data.text} />
									</div>
								{/if}
								{#if data.button_link}
									<div class="button-link">
										<DirectusLink {...data.button_link}>
											<Button {...data.button_link} style="yellow" />
										</DirectusLink>
									</div>
								{/if}
							</div>
						</div>
					{:else if data.type === 'slim'}
						<div class="grid {data.media_position} ">
							<div class="col1">
								<Slider
									name="text-image-{data.id}"
									type="single"
									navigation={false}
									centeredSlides={false}
									pagination={true}
									slidesPerView={1}
								>
									{#each data.items as { section_text_image_item_id: item }, idx}
										<div class="swiper-slide">
											<div class="wrapper">
												<div class="media">
													<Media {...item.media} fit="cover" transform="l" />
												</div>
											</div>
										</div>
									{/each}
								</Slider>
							</div>
							<div class="col2">
								<div class="content">
									<div>
										{#if data.section_title}
											<div class="section-title">{data.section_title}</div>
										{/if}
										{#if data.title}
											<div class="title">{data.title}</div>
										{/if}
									</div>
									{#if data.text}
										<div class="text"><Wysiwyg text={data.text} /></div>
									{/if}

									{#if data.button_link}
										<div class="button-link">
											<DirectusLink {...data.button_link}>
												<Button {...data.button_link} />
											</DirectusLink>
										</div>
									{/if}
								</div>
							</div>
						</div>
					{/if}
				{/if}
			</div>
		{:else if data.type === 'normal'}
			<div class="section-wrapper {data.type}">
				<div class="background" />
				<div class="grid {data.media_position}">
					<div class="col1">
						{#if data.items}
							<div class="image-wrapper">
								{#each data.items as { section_text_image_item_id: item }, idx}
									<div class="media">
										<Media {...item.media} fit="cover" transform="l" />
									</div>
								{/each}
							</div>
						{/if}
					</div>
					<div class="col2">
						<div>
							{#if data.section_title}
								<div class="section-title">{data.section_title}</div>
							{/if}
							{#if data.title}
								<div class="title">{data.title}</div>
							{/if}
						</div>
						<div>
							{#if data.text}
								<div class="text"><Wysiwyg text={data.text} /></div>
							{/if}
							{#if data.button_link}
								<div class="button-link">
									<DirectusLink {...data.button_link}>
										<Button {...data.button_link} />
									</DirectusLink>
								</div>
							{/if}
						</div>
					</div>
				</div>
			</div>
		{:else if data.type === 'large'}
			{#if data.items[0].section_text_image_item_id.media.type === 'image/jpeg'}
				<div class="container">
					<div class="col">
						<div>
							{#if data.section_title}
								<div class="section-title">{data.section_title}</div>
							{/if}
							{#if data.title}
								<div class="title {data.type}">{data.title}</div>
							{/if}
							{#if data.text}
								<div class="text-large-mobile">
									<Wysiwyg text={data.text} />
								</div>
							{/if}
							{#if data.button_link}
								<div class="button-link-mobile">
									<DirectusLink {...data.button_link}>
										<Button {...data.button_link} style="yellow" />
									</DirectusLink>
								</div>
							{/if}
						</div>
						{#each data.items as { section_text_image_item_id: item }, idx}
							<div class="wrapper {data.type}">
								<div class="media">
									<Media {...item.media} fit="cover" transform="l" showAudioButton={false} />
								</div>
							</div>
						{/each}
					</div>
					<div class="text-button-wrapper">
						{#if data.text}
							<div class="text-large">
								<Wysiwyg text={data.text} />
							</div>
						{/if}
						{#if data.button_link}
							<div class="button-link {data.type}">
								<DirectusLink {...data.button_link}>
									<Button {...data.button_link} style="yellow" />
								</DirectusLink>
							</div>
						{/if}
					</div>
				</div>
			{:else}
				<div class="video-container">
					<div class="video-titles">
						<div>
							{#if data.section_title}
								<div class="section-title">{data.section_title}</div>
							{/if}
							{#if data.title}
								<div class="title {data.type}">{data.title}</div>
							{/if}
							{#if data.text}
								<div class="text-large-mobile">
									<Wysiwyg text={data.text} />
								</div>
							{/if}
							{#if data.button_link}
								<div class="button-link-mobile">
									<DirectusLink {...data.button_link}>
										<Button {...data.button_link} style="yellow" />
									</DirectusLink>
								</div>
							{/if}
						</div>
						<div class="text-button-wrapper">
							{#if data.text}
								<div class="text-large">
									<Wysiwyg text={data.text} />
								</div>
							{/if}
							{#if data.button_link}
								<div class="button-link {data.type}">
									<DirectusLink {...data.button_link}>
										<Button {...data.button_link} style="yellow" />
									</DirectusLink>
								</div>
							{/if}
						</div>
					</div>

					{#each data.items as { section_text_image_item_id: item }, idx}
						<div class="video">
							<div class="media">
								<Media {...item.media} fit="cover" transform="l" showAudioButton={false} />
							</div>
						</div>
					{/each}
				</div>
			{/if}
		{:else if data.type === 'slim'}
			<div class="section-wrapper {data.type}">
				<div class="grid {data.media_position} ">
					<div class="col1 {data.type}">
						{#each data.items as { section_text_image_item_id: item }, idx}
							<div class="wrapper {data.items.length > 1 && idx === 0 ? 'first-item' : ''}">
								<div class="media">
									<Media {...item.media} fit="cover" transform="l" />
								</div>
							</div>
						{/each}
					</div>
					<div class="col2">
						<div class="content">
							<div>
								{#if data.section_title}
									<div class="section-title">{data.section_title}</div>
								{/if}
								{#if data.title}
									<div class="title">{data.title}</div>
								{/if}
							</div>
							{#if data.text}
								<div class="text"><Wysiwyg text={data.text} /></div>
							{/if}

							{#if data.button_link}
								<div class="button-link">
									<DirectusLink {...data.button_link}>
										<Button {...data.button_link} />
									</DirectusLink>
								</div>
							{/if}
						</div>
					</div>
				</div>
			</div>
		{/if}
	</Section>
</div>

<style lang="scss">
	.section-text-image {
		position: relative;
		overflow: hidden;
	}
	.background {
		&.normal {
			background: color(grey_02);
			position: absolute;
			z-index: -1;
			width: 100%;
			height: 100%;
			top: 0;
			inset-inline-start: 0;
			transform: translateX(35%);
			@include media(phone) {
				transform: translateX(0);
			}
		}
		&.large {
			background: color(grey_02);
			position: absolute;
			z-index: -1;
			width: 100%;
			height: 60%;
			top: 0;
			inset-inline-start: 0;
			@include media(tablet) {
				height: 100%;
			}
		}
		&.right {
			background: none;
		}
		&.video-section {
			background: none;
		}
	}
	.section-wrapper {
		&.slim {
			:global(.swiper-pagination) {
				bottom: 0;
			}
			padding: 0 v(content-padding-5xl);

			@media (min-width: 1400px) {
				padding: 0 v(content-padding-2xl);
			}
			@media (max-width: 1400px) {
				padding: 0;
			}
		}
		&.normal {
			overflow: visible;
			.media {
				// aspect-ratio: 1/1;
			}
			.grid {
				&.left {
					.col1 {
						display: flex;
						justify-content: flex-end;
					}
				}
			}

			.col2 {
				justify-content: flex-start;

				gap: v(content-gap);
				@include media(phone) {
					padding: 0;
				}
			}
		}
	}
	.grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: v(content-gap-2xl);

		@include media(phone) {
			grid-template-columns: 1fr;
			gap: v(content-gap);
		}
		&.right {
			.col1 {
				order: 2;
				@include media(phone) {
					order: 0;
				}
			}
		}
	}
	.col1 {
		position: relative;
		overflow: hidden;
		&.slim {
			display: flex;
			gap: v(content-gap);
		}
	}
	.image-wrapper {
		display: flex;
		gap: v(content-gap);
		width: 85%;
		max-height: 650px;
		@include media(tablet) {
			width: 100%;
		}
	}
	.col2 {
		display: flex;
		flex-direction: column;
		justify-content: space-around;
		@include media(phone) {
			align-items: flex-start;
		}
	}
	.section-title {
		@include font(h6);
		margin-bottom: v(content-gap-xs);
	}
	.title {
		margin-bottom: v(content-gap);
		max-width: 15ch;
		&.large {
			max-width: 20ch;
		}
		@include font(h2);

		@include media(phone) {
			max-width: 100%;
			margin-bottom: v(content-padding-lg);
		}
	}
	.text {
		@include font(text-sm);
		max-width: 50ch;
		@include media(phone) {
			max-width: 100%;
		}
	}
	.button-link {
		margin-top: v(content-gap);
		&.large {
			@media (max-width: 600px) {
				display: none;
			}
		}
	}
	.button-link-mobile {
		display: none;
		@media (max-width: 600px) {
			display: flex;
			margin-bottom: v(content-padding);
		}
	}
	.container {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: v(content-gap-4xl);
		@media (max-width: 1100px) {
			gap: v(content-gap-xl);
		}
		@media (max-width: 600px) {
			grid-template-columns: repeat(1, minmax(0, 1fr));
			gap: 0;
		}
	}
	.text-large {
		margin-top: v(content-gap-4xl);
		max-width: 500px;
		position: relative;
		@media (max-width: 600px) {
			display: none;
		}
	}
	.col-large {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		position: relative;
		:global(.navigation) {
			position: absolute;
			bottom: 5px;
			inset-inline-end: 0;
			padding-bottom: 0 !important;
			z-index: 5;
		}
		.media {
			position: relative;
			display: flex;
			flex-direction: column;
			aspect-ratio: 1/1;
			@media (min-width: 1800px) {
				aspect-ratio: 8/6;
			}

			:global(img) {
				position: absolute;
				width: 100%;
				height: 100%;
				inset-inline-start: 0;
				bottom: 0;
				top: 0;
			}
		}
		// :global(.navigation) {
		// 	padding-bottom: 0;
		// 	position: absolute;
		// 	transform: translateX(-200px);
		// }
		:global(.swiper-pagination) {
			bottom: 8%;
		}
	}
	.wrapper {
		&.large {
			.media {
				position: relative;
				aspect-ratio: 1/1;
				@media (min-width: 1800px) {
					aspect-ratio: 8/6;
				}

				:global(img) {
					position: absolute;
					width: 100%;
					height: 100%;
					inset-inline-start: 0;
					bottom: 0;
					top: 0;
				}
			}
		}
	}
	.text-large-mobile {
		display: none;
		@media (max-width: 600px) {
			display: flex;
			margin-bottom: v(content-gap);
		}
	}
	.text-button-wrapper {
		display: flex;
		flex-direction: column;
		@media (max-width: 600px) {
			display: none;
		}
	}
	.first-item {
		.media {
			aspect-ratio: 1/1;
		}
	}
	.video-titles {
		display: flex;
		justify-content: space-between;
		margin-bottom: v(content-gap-xl);
		gap: v(content-gap-5xl);
		@include media(phone) {
			margin-bottom: v(content-gap-sm);
		}
		@include media(tablet) {
			gap: 0;
			flex-direction: column;
			.text-large {
				margin-top: 0;
			}
		}
		.title {
			max-width: 15ch;
			@include media(tablet) {
				max-width: 100%;
				margin-bottom: v(content-gap-xs);
			}
		}
		.text-large {
			max-width: 750px;
		}
	}
	.video {
		width: 75%;
		@include media(tablet) {
			width: 100%;
		}
	}
	.media-caption {
		margin-top: v(content-gap-sm);
		@include font(text-sm);
	}
</style>
